import React, { useState } from "react";

import { cn } from "@/lib/utils";

import "./Switch.css";

export interface SwitchProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "onClick" | "type" | "role" | "aria-checked"
> {
  checked: boolean;
  // Return a promise to get the loader, and a shake if it rejects.
  onCheckedChange: (checked: boolean) => void | Promise<unknown>;
}

const ease = "ease-[cubic-bezier(0.23,1,0.32,1)]";

export const Switch: React.FC<SwitchProps> = ({
  checked,
  onCheckedChange,
  disabled = false,
  className,
  ...rest
}) => {
  const [pending, setPending] = useState<boolean | null>(null);
  const [failed, setFailed] = useState(false);
  const saving = pending !== null;
  const shown = pending ?? checked;

  const toggle = () => {
    if (saving) return;
    const next = !shown;
    const result = onCheckedChange(next);
    if (!(result instanceof Promise)) return;
    setFailed(false);
    setPending(next);
    result.then(
      () => setPending(null),
      () => {
        setPending(null);
        setFailed(true);
      },
    );
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={shown}
      aria-busy={saving}
      aria-disabled={saving || undefined}
      disabled={disabled}
      onClick={toggle}
      className={cn(
        "relative inline-flex h-4 w-7 flex-shrink-0 items-center overflow-hidden rounded-full",
        "transition-colors duration-150",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
        "disabled:pointer-events-none disabled:opacity-50",
        shown ? "bg-indigo-600" : "bg-slate-300 dark:bg-slate-700",
        failed &&
          "switch-shake ring-2 ring-rose-500 ring-offset-1 ring-offset-white dark:ring-offset-slate-900",
        className,
      )}
      {...rest}
    >
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-0 transition-opacity duration-150",
          ease,
          saving ? "opacity-100 delay-150" : "opacity-0",
        )}
      >
        {saving && (
          <span className="switch-stripes absolute inset-y-0 -left-7 -right-7" />
        )}
      </span>
      <span
        className={cn(
          "relative inline-block h-3 w-3 transform rounded-full bg-white shadow-sm",
          "transition-[transform,opacity] duration-150",
          ease,
          shown ? "translate-x-[14px]" : "translate-x-0.5",
          saving && "scale-50 opacity-0 delay-150",
        )}
      />
    </button>
  );
};

Switch.displayName = "Switch";
